import { Inject, Injectable, KeyValueDiffers, OnDestroy } from "@angular/core";
import { LogService } from "../../logs/log.service";
import { TemplateService } from "../../templates/shared/template.service";
import { NoteService } from "./note.service";
import { Log, LogRoleId, LogWithTemplates } from "../../logs/log.model";
import { BehaviorSubject, combineLatest, EMPTY, forkJoin, from, merge, Observable, of, race, Subject, throwError, timer } from "rxjs";
import { Template, TemplateSegment, TemplateSegmentTypeId, TemplateTypeId, TemplateWithDrafts, TemplateWithUsers } from "ng/src/app/templates/shared/template.model";
import { catchError, debounceTime, defaultIfEmpty, filter, map, pairwise, skip, startWith, switchMap, take, takeUntil, tap, throttleTime } from "rxjs/operators";
import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import { AppStateService } from "../../core/services/app-state.service";
import { ObservableSource, Optional } from "../../shared/model/app.model";
import { LogBookUser, LogUser, LogUserWithLog } from "../../users/shared/user.model";
import { ShiftsService } from "../../shifts/shifts.service";
import { AutoSyncType, CalendarControl, ChoiceListItemFetch, CurrentLogResult, InitModel, LogUserSelection, NoteEntryGroup, NoteEntryInput, NoteEntryNote, NoteEntryStartConfig, NoteEntryTemplateValue, NotePropertyControls, NoteStatusUpdate, NoteSyncErrorFeedback, SectionValidationStatus, SegmentSetupOptions, SegmentTreeNode, SegmentTreeNodeMap, SyncType, ValidationOptions } from "./note-entry.model";
import { Utils } from "../../core/utils";
import { TemplateValueValidators } from "../note-entry/validators/template-value-validator";
import { ChoiceListItemLike } from "../../choice-lists/shared/choice-list.model";
import { ChoiceListService } from "../../choice-lists/shared/choice-list.service";
import { TagService } from "ng/src/app/tags/tag.service";
import { UserService } from "ng/src/app/users/shared/user.service";
import { Note, NoteEntity } from "ng/src/app/notes/shared/note.model";
import { LogUserProjections, NoteProjections, TemplateProjections } from "app/shared/model/request-parameter.model";
import { NoteTemplateValue } from "ng/src/app/templates/shared/notetemplatevalue.model";
import { ILocationService } from "angular";
import { ErrorMap, SegmentDependencyGraph, SegmentMap, SelectedTagMap, TemplateValueCalculator, TemplateValueCalculatorValueMap } from "ng/src/app/templates/shared/template-value-calculator.model";
import { TagValue } from "ng/src/app/tags/tag.model";
import moment from "moment-timezone/moment-timezone-utils";
import { PagedArray } from "app/shared/model/app.model";
import { CustomErrorProperties } from "../../shared/model/error.model";
import { HasId } from "app/shared/model/properties.model";
import { ValueDiffer } from "ng/src/app/core/value-differ";

@Injectable()
export class NoteEntryService implements OnDestroy {
    
    private logsSubject = new BehaviorSubject<LogWithTemplates[]>([]);
    readonly logs$ = this.logsSubject.asObservable();
    get logs() {
        return [...this.logsSubject.value];
    }
    set logs(val) {
        this.templateMap = val.reduce((map, log) => {
            return {...map, ...Utils.arrayToMap(log.templates, t => t.id)}
        }, {});
        
        this.logsSubject.next(val);
    }
    
    private readonly coCreatorsSubject = new BehaviorSubject<LogUserSelection[]>([]);
    coCreators$ = this.coCreatorsSubject.asObservable();
    get coCreators() {
        return this.coCreatorsSubject.value;
    }
    set coCreators(val) {
        this.coCreatorsSubject.next(val);
    }
    
    private selectedTemplateSubject = new BehaviorSubject<TemplateWithUsers | undefined>(undefined);
    readonly selectedTemplate$ = this.selectedTemplateSubject.asObservable();
    get selectedTemplate() {
        return this.selectedTemplateSubject.value;
    }
	private setSelectedTemplate(template: TemplateWithUsers | undefined) {
		return this.selectedTemplateSubject.next(template);
	}
    
    private selectedLogSubject = new BehaviorSubject<Log | LogWithTemplates | undefined>(undefined);
    readonly selectedLog$ = this.selectedLogSubject.asObservable();
    get selectedLog() {
        return this.selectedLogSubject.value;
    }
	private setSelectedLog(log: Log | LogWithTemplates | undefined) {
		this.selectedLogSubject.next(log);
	}
    
    private selectedSegmentsSubject = new BehaviorSubject<TemplateSegment[]>([]);
    readonly selectedSegments$ = this.selectedSegmentsSubject.asObservable();
    get selectedSegments() {
        return [...this.selectedSegmentsSubject.value];
    }
    set selectedSegments(val) {
        this.selectedSegmentsSubject.next(val);
    }

	private logUsersSubject = new BehaviorSubject<LogUserWithLog[]>([]);
	readonly logUsers$ = this.logUsersSubject.asObservable();
	get logUsers() {
		return this.logUsersSubject.value;
	}
	set logUsers(val) {
		this.logUsersSubject.next(val);
	}

	private tagsSubject = new BehaviorSubject<TagValue[]>([]);
	readonly tagValues$ = this.tagsSubject.asObservable();
	get tagValues() {
		return this.tagsSubject.value;
	}
	set tagValues(val) {
		this.tagsSubject.next(val);
	}
    
    private notePropertiesSubject = new BehaviorSubject<NotePropertyControls | undefined>(undefined);
    readonly noteProperties$ = this.notePropertiesSubject.asObservable();
    get noteProperties() {
        return this.notePropertiesSubject.value;
    }
    set noteProperties(val) {
        this.notePropertiesSubject.next(val);
    }
    
    get selectedTemplateType() {
        return this.selectedTemplate?.templateType?.id ?? TemplateTypeId.OPERATIONAL;
    }
    
    private selectedGroupSubject = new BehaviorSubject<NoteEntryGroup | undefined>(undefined);
    readonly selectedGroup$ = this.selectedGroupSubject.asObservable();
	get selectedGroup() {
		return this.selectedGroupSubject.value;
	}
	set selectedGroup(val) {
		this.selectGroup(val)
	}

	private readonly editingNoteSubject = new BehaviorSubject<Note.Projections.TemplateValues | undefined>(undefined);
	readonly editingNote$ = this.editingNoteSubject.asObservable();
	get editingNote() {
		return this.editingNoteSubject.value;
	}
	set editingNote(val) {
		this.editingNoteSubject.next(val);
	}
    
    private noteStatusSubject = new BehaviorSubject(SectionValidationStatus.OK);
    readonly noteStatus$ = this.noteStatusSubject.asObservable();
    get noteStatus() {
        return this.noteStatusSubject.value;
    }
    set noteStatus(val) {
        this.noteStatusSubject.next(val);
    }
    
    private _entryNote = NoteEntryNote.new();
    get entryNote() {
        return this._entryNote;
    }
    set entryNote(val) {
        this._entryNote = val;
        this.updateEntryNoteTemplate(this.selectedGroup);
    }
    
    get editingValues(): NoteTemplateValue[] {
        return this.editingNote?.templateValues ?? [];
    }
    
    private loadingTemplateSubject = new BehaviorSubject<boolean>(false);
	readonly isLoadingTemplate$ = this.loadingTemplateSubject.asObservable();
    get isLoadingTemplate() {
        return this.loadingTemplateSubject.value;
    }
	private setIsLoadingTemplate(val: boolean) {
		this.loadingTemplateSubject.next(val);
	}
    
    private syncingNoteSubject = new BehaviorSubject<boolean>(false);
    isSyncingNote$ = this.syncingNoteSubject.asObservable();
    get isSyncingNote() {
        return this.syncingNoteSubject.value;
    }

	private syncErrorFeedbackSubject = new BehaviorSubject<NoteSyncErrorFeedback | null>(null);
	syncErrorFeedback$ = this.syncErrorFeedbackSubject.asObservable();
	get syncErrorFeedback() {
		return this.syncErrorFeedbackSubject.value;
	}

	private pendingChangesSubject = new BehaviorSubject<boolean>(false);
	pendingChanges$ = this.pendingChangesSubject.asObservable();
	get hasPendingChanges() {
		return this.pendingChangesSubject.value;
	}
	set hasPendingChanges(val) {
		this.pendingChangesSubject.next(val);
	}

	private hasSectionChangesSubject = new BehaviorSubject<boolean>(false);
	pendingSectionChanges$ = this.pendingChangesSubject.asObservable();
	get hasPendingSectionChanges() {
		return this.hasSectionChangesSubject.value;
	}
	set hasPendingSectionChanges(val) {
		this.hasSectionChangesSubject.next(val);
	}

	private isReadySubject = new BehaviorSubject<boolean>(false);
	isReady$ = this.isReadySubject.asObservable();
	get isReady() {
		return this.isReadySubject.value;
	}

	get editingPublishedNote() {
		return !(this.editingNote?.draft ?? true);
	}
	
	readonly noteStatusChangeSubject = new Subject<NoteStatusUpdate>();
    
    allowMultiLog = false;
    templateMap: { [templateId: number]: TemplateWithDrafts } = {};
    treeNodeMap: SegmentTreeNodeMap = { nodes: [], map: {} };
    groupsMap: { [groupSegmentId: number]: NoteEntryGroup } = {};
    segmentDependencyGraph?: SegmentDependencyGraph;
    segmentMap: SegmentMap = {};
    calculationDiffer?: ValueDiffer<string, any>;
    defaultTemplateId: number = NaN;
    initialQueries: {
        choiceLists: { [listId: number]: Observable<PagedArray<ChoiceListItemLike>> },
        tagValues: { [segmentId: number]: Observable<PagedArray<TagValue>> },
        logbookUsers: { [logId: number]: Observable<PagedArray<LogBookUser>> }
    } = { choiceLists: {}, tagValues: {}, logbookUsers: {} };
	initialSizes: {
		choiceLists: { [listId: number]: number },
		tagValues: { [segmentId: number]: number },
		logbookUsers: { [logId: number]: number }
	} = { choiceLists: {}, tagValues: {}, logbookUsers: {} };
    choiceListValues: { [listId: number]: { [value: string]: ChoiceListItemLike } } = {};
	calculationErrors: ErrorMap = {};
	locationUpdatesEnabled = true;
    
    inputs: { [segmentId: number]: NoteEntryInput } = {};
    issueNodes = {
        missingValues: new Array<SegmentTreeNode>(),
        abnormalValues: new Array<SegmentTreeNode>()
    }
    calculationTooltips: { [segmentId: number]: string } = {};
    ntvMap: { [segmentId: number]: NoteTemplateValue } = {};
	syncDate: Date | null = null;
	calendarControls: { [segmentId:number]: CalendarControl } = {};
	viewIsNarrow = false;
 
	private editTemplateSubject = new Subject<number>();
	private editNoteIdSubject = new Subject<number>();
	private editNoteSubject = new Subject<Note.Projections.TemplateValues>();
	private editSectionIdSubject = new BehaviorSubject<number>(-1);
    private groups: NoteEntryGroup[] = [];
    private destroySubject = new Subject<void>();
	
	readonly destroy$ = this.destroySubject.asObservable();

    constructor(
        private http: HttpClient,
        private logService: LogService,
        private templateService: TemplateService,
        public noteService: NoteService,
        private shiftService: ShiftsService,
        private choiceListService: ChoiceListService,
        private tagService: TagService,
        private userService: UserService,
        private differs: KeyValueDiffers,
        @Inject('templateValueCalculator')
        private templateValueCalculator: TemplateValueCalculator,
        @Inject('$location')
        public $location: ILocationService)
    {
		combineLatest([
			merge(
				this.editTemplateSubject.pipe(
					switchMap(template => this.getTemplate(template))
				),
				this.editNoteSubject,
				this.editNoteIdSubject.pipe(
					switchMap(noteId => this.noteService.getLogNote(noteId, NoteProjections.templateValues))
				)
			)
				.pipe(startWith(null)),
			this.isReady$
		])
			.pipe(
				takeUntil(this.destroy$),
				filter(([, isReady]) => isReady),
				switchMap(([next]) => {
					const model: ObservableSource<InitModel> = {
						template: of(null),
						note: of(null),
						section: this.editSectionIdSubject.pipe(take(1))
				};

					if (next && 'note' in next) {
						const note = next;
						if (note) {
							this.setIsLoadingTemplate(true);
							model.template = this.getTemplate(note.templateId ?? null, note.logId);
							model.note = of(note);
							model.globalSubstituteLog = this.getLog(note.logId);
							if (note.subTemplateId) model.section = of(note.subTemplateId);
						}
					}
					else if (next && 'allowedUsers' in next) {
						const template = next;
						model.template = of(template);
					}
					else if (this.selectedLog) {
						const logId = this.selectedLog.id;
						return this.getSearchParamsTemplate().pipe(
							switchMap((searchTemplate) => {
								model.template = searchTemplate ? of(searchTemplate) : this.getDefaultTemplateForActiveUser(logId);
								return forkJoin(model);
							})
						)
					}

					return forkJoin(model);
			}),
				switchMap(({ template, note, section, globalSubstituteLog }: InitModel) => {
					const usedSegments = note?.templateValues.map(ntv => { return { id: ntv.templateSegmentId } });
					return this.setTemplate(template, globalSubstituteLog, usedSegments)
						.pipe(
							switchMap(() => (note) ? from(this.updateEditorFromNote(note, SyncType.SYNC)) : of(template)),
							switchMap(() => combineLatest([
								this.editSectionIdSubject.pipe(
									skip(1),
									startWith(section)
								),
								this.isLoadingTemplate$
							]).pipe(
								filter(([, loading])=> !loading && !!this.selectedTemplate)
							)),
							tap(([editSectionId]) => {
								const node = this.treeNodeMap.map[editSectionId];
								let group = Optional.of<NoteEntryGroup>();
								if (!node && (template?.templateType.id === TemplateTypeId.INSPECTION || this.treeNodeMap.nodes.length === 1)) {
									group = this.groupsMap[this.findNextBestNode(this.treeNodeMap.nodes[0])?.data.id ?? -1];
								}
								else if (node) {
									group = this.groupsMap[node.data.id];
								}
								this.selectGroup(group, false, false);
							})
						)
				})
			)
			.subscribe();
    }

    ngOnDestroy() {
        this.destroySubject.next();
    }
    
    editNote(note: Note.Projections.TemplateValues) {
		this.editNoteSubject.next(note);
    }
    
    editNoteId(noteId: number) {
        this.editNoteIdSubject.next(noteId);
    }

	editSectionId(sectionId: number) {
		this.editSectionIdSubject.next(sectionId);
	}
	
	editTemplate(template: number) {
		this.editTemplateSubject.next(template);
	}

    start(initialConfig: NoteEntryStartConfig | null = null) {
        this.isReadySubject.next(false);
		const multiLog = this.allowMultiLog;
		
		if (initialConfig?.disableLocationModification)
			this.locationUpdatesEnabled = false;
		
		forkJoin([
            initialConfig?.selectedLog ? of([]) : this.logService.getLogsWithTemplates(multiLog ? undefined : LogService.selectedLog?.id),
            this.getWritableLogs(),
        ])
            .pipe(
                switchMap(([logs, writableLogs]) => {
                    this.logs = logs.filter(l => writableLogs.find(wl => wl.log.id === l.id))
                        .sort(Utils.createSortFunction(l => l.name));
					
					if (initialConfig?.selectedLog) {
						return this.setupLog(initialConfig.selectedLog);
					}
					return (multiLog) ? this.setupLog(null) : this.setupLog(logs[0]);
                }),
				tap(() => this.isReadySubject.next(true)),
				takeUntil(
					race(
						this.destroy$,
						this.isReady$.pipe(
							skip(1),
							filter(isReady => !isReady)
						)
					)
				)
            )
			.subscribe();
    }

	setTemplate(template: TemplateWithUsers | number | undefined | null, globalTemplateSubstituteLog: Log | null = null, allowedObsoleteSegments: HasId[] = [], enforceAccess = false): Observable<CurrentLogResult> {
		return this.setupTemplate(template, globalTemplateSubstituteLog, allowedObsoleteSegments)
			.pipe(
				map(template => (!enforceAccess || (template && TemplateWithUsers.currentUserCanAccess(template.template))) ? template : null),
				switchMap((result) => {
					return ((result?.template) 
						? this.setupLog(result.isGlobal ? globalTemplateSubstituteLog : result?.template?.logId) 
						: of({ 
							log: this.selectedLog ?? null, 
							logUsers: this.logUsers
						}))
				}),
				tap(() => {
					this.updateLogUsersCoCreatorSelection(this.logUsers);
				})
			);
	}

	setLog(log: Log | number | undefined | null): Observable<CurrentLogResult> {
		return this.setupTemplate(null)
			.pipe(
				switchMap(() => this.setupLog(log)
			));
	}

	setToDefaultTemplate() {
		let ob$: Observable<TemplateWithUsers | null | undefined>;

		if (!this.selectedLog) 
			ob$ = of(null);
		else {
			ob$ = this.getDefaultTemplateForActiveUser(this.selectedLog)
		}
		return ob$.pipe(
			switchMap(template => this.setTemplate(template))
		)
	}

	private isLogAllowed(log: Log | number | undefined | null) {
		const logId = typeof log === 'number' ? log : log?.id;
		return (this.allowMultiLog || !this.selectedLog || this.selectedLog?.id === logId);
	}
    
    private updateLogUsersCoCreatorSelection(logUsers: LogUser[]) {
        const activeUserId = LogService.selectedLog?.activeLogUser?.user?.id;
        const coCreatorSrc = this.editingNote ? this.editingNote.coCreators : this.selectedTemplate?.defaultCoCreators ?? [];
        const coCreatorMap = Utils.arrayToMap(coCreatorSrc, lgUsr => lgUsr.id);
    
        this.coCreators = logUsers.map(lgUser => {
            return {
                ...lgUser,
                selected: activeUserId === lgUser.user.id || lgUser.id in coCreatorMap
            }
        });
    }
    
    private updateEntryNoteTemplate(group: NoteEntryGroup | undefined) {
        this.entryNote.template = this.selectedTemplate?.isDefaultTemplate ? undefined : this.selectedTemplate?._links.self.href;
        if ((group?.segmentId ?? -1) > 0 && this.selectedTemplate?.templateType.id === TemplateTypeId.OPERATIONAL) {
            this.entryNote.subTemplate = this.selectedSegments.find(segment => segment.id === group?.segmentId)?._links.self.href;
        }
    }
    
    changeGroup(direction: 'NEXT' | 'PREVIOUS') {
        if (!this.selectedGroup)
            return;
        
        const selectedGroup = this.getNextSection(direction);
        
        if (!selectedGroup)
            return;
        
        this.selectGroup(this.groupsMap[(selectedGroup.segmentId ?? -1)]);
    }

	selectGroup(group: Optional<NoteEntryGroup>, triggerSync = true, clearOperationalValues = true): void {
		if (this.selectedTemplateType === TemplateTypeId.OPERATIONAL) {
			if (clearOperationalValues) this.clearValues();
			
			const currentSegment = this.segmentMap[group?.segmentId ?? -1];
			if (currentSegment && this.noteProperties) {
				this.noteProperties.turnover = currentSegment.turnover ?? false;
			}
			
			this.hasPendingChanges = false;
		}
		else if (triggerSync && group) {
			this.triggerSync(AutoSyncType.SECTION_CHANGE);
		}
		
		this.hasPendingSectionChanges = false;
		this.updateEntryNoteTemplate(group);
        this.selectedGroupSubject.next(group);
	}
    
    private setTemplateInLocation(templateId?: number) {
		if (!this.locationUpdatesEnabled) return;
        this.$location.search('template', templateId ?? null).replace();
    }
    
    private reset() {
        this.inputs = {};
        this.ntvMap = {};
        this.groups = [];
        this.groupsMap = {};
        this.treeNodeMap = { nodes: [], map: {}};
        this.noteStatus = SectionValidationStatus.OK;
        this.issueNodes = { missingValues: [], abnormalValues: [] };
        this.segmentMap = {};
        this.initialQueries = { choiceLists: {}, tagValues: {}, logbookUsers: {} };
		this.initialSizes = { choiceLists: {}, tagValues: {}, logbookUsers: {} };
        this.choiceListValues = {};
        this.syncingNoteSubject.next(false);
        this.syncDate = null;
		this.syncErrorFeedbackSubject.next(null);
		this.hasPendingChanges = false;
		this.hasPendingSectionChanges = false;
		this.entryNote = NoteEntryNote.new();
        this.selectedGroup = undefined;
		this.editSectionIdSubject.next(-1);
		this.editingNote = undefined;
		this.noteProperties = undefined;
		this.calendarControls = {};
		this.calculationErrors = {};

        delete this.calculationDiffer;
    }
    
    private clearValues() {
        Object.values(this.inputs)
            .forEach(control => {
				control.clearErrors('ALL');
	            control.setValue(this.getDefaultControlValue(control.templateSegment), {
		            emit: false,
		            validationOptions: {allowedMessageTypes: ['warn']}
	            })
            });
    }

	private setupLog(log: Log | number | undefined | null): Observable<CurrentLogResult> {
		const noLogChange = of({
			log: this.selectedLog ?? null,
			logUsers: this.logUsers
		});

		if (!this.isLogAllowed(log))
			return noLogChange; // No Log Change
		else if (this.selectedLog && (log || typeof log === 'number')) {
			const logId = typeof log === 'number' ? log : log.id;
			if (this.selectedLog?.id === logId)
				return noLogChange; // No Log Change
		}
		else if (!log && this.selectedLog?.id == null) {
			return noLogChange; // No Log Change
		}
		
		const logId = typeof log === 'number' ? log : log?.id;
		return forkJoin({
			log: logId == null ? of(null) : this.getLog(logId),
			logUsers: logId == null ? of([]) : this.userService.fetchLogUsers({ 
				log: logId,
				hasHistoricalUser: true,
				sort: ['user.lastName', 'user.firstName']
			})
		} as ObservableSource<CurrentLogResult>).pipe(
			tap(({log, logUsers}) => {
				this.logUsers = logUsers;
				this.setSelectedLog(log ?? undefined);
			})
		);
	}

	private setupTemplate(template: TemplateWithUsers | number | undefined | null, globalTemplateSubstituteLog: Log | null = null, allowedObsoleteSegments: HasId[] = []): Observable<{template: TemplateWithUsers, isGlobal: boolean } | null> {
		if (!template && this.selectedTemplate?.id == null) {
			return of(null);
		}

		let getTemplate$: Observable<TemplateWithUsers | null>;
		if (template == null)
			getTemplate$ = of(null);
		else {
			const logId = typeof template === 'number' ? null : template.logId;
			const templateId = typeof template === 'number' ? template : template.id;
			getTemplate$ = this.getTemplate(templateId, logId).pipe(map(t => t ?? null));
		}
		
		if (template) this.setIsLoadingTemplate(true);
		return getTemplate$.pipe(
			switchMap(template => {
				
				return (template == null)
					? of({isGlobal: false, template})
					: this.templateService.isTemplateGlobalTemplate(template).pipe(
						map((isGlobal) => { return { isGlobal, template } })
					);
			}),
			takeUntil(this.isLoadingTemplate$.pipe(
				skip(1),
				filter(val => val)
			)),
			switchMap(({isGlobal, template}) => forkJoin({
				template: of(template),
				log: isGlobal ? of(globalTemplateSubstituteLog) : template ? this.getLog(template?.logId) : of(null),
				isGlobal: of(isGlobal)
			})),
			switchMap(({template, log, isGlobal}) => {
				this.reset();

				if (!template || !log || !this.isLogAllowed(log)) {
					this.setSelectedTemplate(template ?? undefined);
					return of(null);
				}

				return this.templateService.fetchTemplateSegments(template.id)
					.pipe(
						map(segments => {
							this.setSelectedTemplate(template);
							this.selectedSegments = segments;
							this.setupSegments({
								template: template,
								overrideLogId: log?.id,
								introSectionName: `Intro to ${template.name}`,
								segments: segments,
								allowedObsoleteSegments: allowedObsoleteSegments
							});
							this.setupNotePropertiesControls(template, segments);
							this.setupValueObservers();

							this.entryNote = NoteEntryNote.fromTemplate(template);
							this.entryNote.log = log._links.self.href;

							return { template, isGlobal };
						})
					)
			}),
			tap((result) => this.setTemplateInLocation(result?.template?.id ?? undefined)),
			tap(() => this.setIsLoadingTemplate(false))
		)
	}

    private setupSegments({ template, overrideLogId, introSectionName, allowedObsoleteSegments = [], segments = [] }: SegmentSetupOptions) {
        this.segmentMap = {};
        if (!template || !segments.length) {
            return;
        }
				
				const allowedSegmentMap = Utils.arrayToMap(allowedObsoleteSegments, 'id');
				const templateContainer = this.templateService.buildTemplateContainer(template, segments);
    
        let currentGroup: NoteEntryGroup = { segmentId: undefined, controls: [], templateType: template.templateType.id };
        
        segments.forEach((segment, idx) => {
            this.segmentMap[segment.id] = segment;
            
            if (segment.type.id === TemplateSegmentTypeId.SUB_TEMPLATE) {
                if (currentGroup.segmentId || currentGroup.controls.length)
                    this.groups.push(currentGroup);
            
                currentGroup = { segmentId: segment.id, controls: [], templateType: template.templateType.id };
            } else {
                if (isValidNoteEntrySegment.call(this, segment)) {
                    const control = this.inputs[segment.id] = this.createEntryControl(segment, overrideLogId ?? template.logId);
                    currentGroup.controls.push(control);
                }
                if (idx === segments.length - 1)
                    this.groups.push(currentGroup);
            }
        });
    
        this.groupsMap = Utils.arrayToMap(this.groups, g => g.segmentId ?? -1);
        this.createNodesFromSegments(introSectionName ?? template.name);
        this.createCommentControls(template);
        
        this.segmentDependencyGraph = this.templateValueCalculator.calculateDependencyGraph(segments);
	    
	    function isValidNoteEntrySegment(this: NoteEntryService, segment: TemplateSegment) {
		    const filterIds = [
			    TemplateSegmentTypeId.SUB_TEMPLATE,
			    TemplateSegmentTypeId.SIGNOFF,
			    TemplateSegmentTypeId.TAG,
			    TemplateSegmentTypeId.SIGNATURE,
		    ];
		    
		    const segmentIsObsolete = segment.obsolete && !(segment.id in allowedSegmentMap);
				
				// If a template segment in an operational sub template belongs to an obsolete sub template, consider it obsolete
		    // if it is not explicitly allowed.
				if (templateContainer.templateTypeId === TemplateTypeId.OPERATIONAL) {
					const segmentEntry = templateContainer.getById(segment.id);
					if (segmentEntry?.parent?.segment?.obsolete === true && !(segment.id in allowedSegmentMap)) {
						return false;
					}
				}
		    
		    return !(segmentIsObsolete || (segment.type.id === TemplateSegmentTypeId.LITERAL_TEXT && template!.templateType.id === TemplateTypeId.OPERATIONAL) ||
			    filterIds.some(f => segment.type.id === f));
	    }
    }

	private setupValueObservers() {
		const syncSub = combineLatest(Object.values(this.inputs).map(i => i.onChange$
			.pipe(
				tap(() => {
					if (!this.isLoadingTemplate) {
						this.hasPendingSectionChanges = true;
						console.log(`${i.templateSegment.name} => `, i.value);
					}
				})
			)))
			.pipe(
				filter(() => !this.isLoadingTemplate)
			)
			.subscribe(() => this.triggerSync(AutoSyncType.AUTO));
		
		const validationSub = combineLatest(
			Object.values(this.inputs).map(i => i.onValidation$)
		)
			.pipe(
				debounceTime(100)
			)
			.subscribe(() => this.updateNoteStatus());
		
		combineLatest(
			Object.values(this.inputs)
				.filter(i => !i.templateSegment.calculate)
				.map(i => i.value$.pipe(
					startWith(i.value as unknown),
					pairwise(),
					tap(([prev, current]) => {
						if (i.templateSegmentType !== TemplateSegmentTypeId.TAG_CHOICE) return;
						
						const update = { add: new Array<TagValue>(), remove: new Array<TagValue>() };
						const prevMapped = this.makeTagMap(prev);
						const currentMapped = this.makeTagMap(current);
						
						update.add = notIn(current, prevMapped);
						update.remove = notIn(prev, currentMapped);
						
						this.updateNotePropertyTagsFrom(update);
						
						function notIn(tags: TagValue[], checking: Record<string, TagValue>) {
							return tags.filter(tag => !(tag.id in checking));
						}
					})
				))
		)
			.pipe(
				takeUntil(
					race(
						this.selectedTemplate$.pipe(skip(1)),
						this.destroy$
					)
				)
			).subscribe(() => {
				this.calculateValues();
			})
			.add(syncSub)
			.add(validationSub);
	}
	
	private makeTagMap(tags: any) {
		return Utils.arrayToMap<TagValue>( Array.isArray(tags) ? tags : [], tag => tag.id );
	}
	
	private updateNotePropertyTagsFrom(update: { add: TagValue[], remove: TagValue[] }) {
		if (!this.noteProperties) return;
		
		const propertyTags = this.makeTagMap(this.noteProperties.tagValues);
		update.add.forEach(tag => (propertyTags[tag.id] = tag));
		update.remove.forEach(tag => ( delete propertyTags[tag.id] ));
		
		this.noteProperties.tagValues = Object.values(propertyTags);
	}

	private setupNotePropertiesControls(template?: TemplateWithUsers, segments?: TemplateSegment[]) {
        let endDate: Date | null = null;
        if ((template?.endCarryForwardDays ?? 0) > 0) {
            endDate = new Date();
            endDate.setDate(endDate.getDate() + template?.endCarryForwardDays!);
        }
        
        this.noteProperties = {
            turnover: template?.turnover ?? false,
            carryforward: {
                enabled: template?.carryForward ?? false,
                endDate: endDate
            },
            noteTime: null,
            note: { useRichText: true, model: '' },
            attachments: [],
            tagValues: []
        }
		
		if (segments?.length)
			this.updateNotePropertyTagsFrom({
				remove: [],
				add: segments.filter(s => s.type.id === TemplateSegmentTypeId.TAG && s.tagValue!!)
					.map(s => s.tagValue!)
			});
    }

	private getLog(logId: number): Observable<Log | LogWithTemplates | null>;
	private getLog(logId: number, requestWithTemplates: true): Observable<LogWithTemplates | null>;
	private getLog(logId: number, requestWithTemplates: boolean = false): Observable<Log | LogWithTemplates | null> {
		if (isNaN(logId)) return of(null).pipe(defaultIfEmpty(null));

		const log = this.logs.find(log => log.id === logId);
		
		if (log) return of(log);
		const ob$ = (!requestWithTemplates)
			? this.logService.getLogById(logId)
			: this.logService.getLogsWithTemplates(logId)
				.pipe(
					map(logs => logs[0] ?? null)
				);
		
		return (ob$ as Observable<any>).pipe(defaultIfEmpty(null));
	}

	private getTemplate(templateId: number | null, logId: number | null = null): Observable<TemplateWithUsers | null | undefined> {
		if (templateId == null && logId == null) return of(null);
		
		return ((typeof logId === 'number')
			? this.getLog(logId, true)
			: EMPTY.pipe(defaultIfEmpty(null))
		)
			.pipe(
				switchMap(log => {
					let ob$: Observable<TemplateWithUsers | null | undefined> = EMPTY;
					
					if (log && templateId == null) {
						const template = log.templates.find(template => !!template.isDefaultTemplate);
						ob$ = (template) ? of(template) : this.templateService.getBasicNoteTemplate(log.id, TemplateProjections.WITH_USERS);
					}
					else if (log && typeof templateId === 'number') {
						const template = log.templates.find(template => template.id === templateId);
						ob$ = template ? of(template) : this.templateService.getTemplateById(templateId, TemplateProjections.WITH_USERS);
					}
					else if (typeof templateId === 'number' && !isNaN(templateId)) {
						for (const log of this.logs) {
							const lTemplate = log.templates.find(t => t.id === templateId);
							if (lTemplate)
								ob$ = of(lTemplate);
						}
						ob$ = this.templateService.getTemplateById(templateId, TemplateProjections.WITH_USERS);
					}
					
					return ob$.pipe(defaultIfEmpty(null as any));
				})
			);
	}

	private getDefaultTemplateForActiveUser(log: Log | number, enforceAccess = true): Observable<TemplateWithUsers | null | undefined> {
		const activeUserId = LogService.selectedLog?.activeLogUser?.user?.id;
		const logId = typeof log === 'number' ? log : log.id;

		if (typeof activeUserId !== 'number' || isNaN(logId)) return of(null);

		
		return this.logUsers$.pipe(
			switchMap(logUsers => {
				const sameLog = logUsers[0]?.log?.id === logId;
				if (sameLog) {
					return of(logUsers?.find(lgUser => lgUser.user.id === activeUserId));
				}
				else {
					return this.userService.fetchLogUsers({ 
						'user.id': activeUserId,
						'logId': logId
					}, LogUserProjections.WITH_LOG)
					.pipe(map(logUsers => logUsers[0]))
				}
			}),
			switchMap(logUser => {
				const defaultTemplateId = logUser?.defaultTemplate?.id ?? logUser?.log?.defaultTemplate?.id;
				return (typeof defaultTemplateId === 'number') ? this.getTemplate(defaultTemplateId) : of(null);
			}),
			map((template) => !enforceAccess || (template && TemplateWithUsers.currentUserCanAccess(template)) ? template : null),
			take(1)
		)
	}

	private getSearchParamsTemplate() {
		const locTemplateId = parseInt(this.$location.search()['template']);
		return this.getTemplate(locTemplateId).pipe(
			map(template => {
				return (!template || !TemplateWithUsers.currentUserCanAccess(template)) ? null : template;
			})
		);
	}

	private autoSyncSubject: Subject<AutoSyncType> = (() => {
		const subject = new Subject<AutoSyncType>(); 
		subject.asObservable()
			.pipe(
				takeUntil(this.destroy$),
				switchMap((autoSyncType) => {
					if (autoSyncType === AutoSyncType.SECTION_CHANGE && !this.hasPendingSectionChanges && !this.editingNote)
						return EMPTY;
					else if (autoSyncType !== AutoSyncType.SECTION_CHANGE)
						this.hasPendingChanges = true;
					
					this.calculateValues();
					return timer(autoSyncType === AutoSyncType.FORCE || autoSyncType === AutoSyncType.SECTION_CHANGE ? 0 : 5000)
					.pipe(
							take(1),
							switchMap(() => of({ hasPendingChanges: this.hasPendingChanges, syncType: autoSyncType}))
					)
				}),
				filter(() => this.selectedTemplateType === TemplateTypeId.INSPECTION),
				switchMap(() => {
					const syncType = (this.hasPendingChanges && !this.editingPublishedNote) ? SyncType.SYNC : SyncType.UPDATE_ONLY;
					return this.syncNote(syncType).pipe(
						tap(() => {
							if (syncType === SyncType.SYNC)
								this.hasPendingChanges = false;
						}),
						catchError(err => of(err))
					);
				}),
				catchError(err => of(err))
			)
			.subscribe();
			return subject;
	})();

	triggerSync(type: AutoSyncType) {
		this.autoSyncSubject.next(type);
	}

    private createEntryControl(segment: TemplateSegment, logId: number): NoteEntryInput {
		const initVal = this.getDefaultControlValue(segment);

		// Sets entered date so that default value can be synced
		const initDate = (segment.type.id === TemplateSegmentTypeId.TEXT_CHOICE && initVal !== null) 
			? new Date(0) 
			: null; 

        const control = new NoteEntryInput(logId, segment, initVal, initDate)
            .addValueValidators(...TemplateValueValidators.getValidators(segment))
            .setErrorMessageProvider(TemplateValueValidators.getMessageProvider)
            .addExtra('useRichText', segment.textOption == 'DEFAULT_TO_RICH_TEXT');
        
        control.processValueValidators({ allowedMessageTypes: ['warn'] });

        return control;
    }
    
    private readonly checkValueChangesSubject = (() => {
        const subject = new Subject();
        subject.pipe(
            throttleTime(200),
            takeUntil(this.destroy$)
        )
            .subscribe(() => this.processValueChanges())
        return subject;
    })();
    
    
    checkForValueChanges() {
        this.checkValueChangesSubject.next();
    }
    
    calculationValue(input: NoteEntryInput): any {
        let value = input.value;
    
        switch (input.templateSegmentType) {
            case TemplateSegmentTypeId.CHECKBOX:
                value = typeof value === 'boolean' ? value ? 'checked' : 'unchecked' : null;
                break;
            case TemplateSegmentTypeId.TAG_CHOICE:
                const tagValues = (Array.isArray(value) ? value : []).filter(tv => tv?._links);
                value = tagValues.map(val => val?.stringValue ?? '')
                    .filter(str => str.trim().length > 1);
                break;
            case TemplateSegmentTypeId.TEXT_CHOICE:
                value = value?.value ?? '';
                break;
            case TemplateSegmentTypeId.DATE_TIME:
                value = value instanceof Date ? moment(value) : null;
                break;
            case TemplateSegmentTypeId.USER_CHOOSER:
                value = value?.id ? [value.id] : [];
        }
        
        return value;
    }
    
    updateNoteStatus() {
        const statusLevels = {
            'green': 0,
            'yellow': 1,
            'red': 2
        }
        
        const issueNodes = {
            missingValues: new Array<SegmentTreeNode>(),
            abnormalValues: new Array<SegmentTreeNode>()
        };
        let status = SectionValidationStatus.OK;

		const groups: SegmentTreeNode[] = [];
		const selectedGroupId = this.selectedGroup?.segmentId ?? -1;
		if (this.selectedTemplateType === TemplateTypeId.OPERATIONAL && this.selectedGroup &&
			selectedGroupId in this.treeNodeMap.map) {
			groups.push(...[this.treeNodeMap.map[selectedGroupId]])
		}
		else if (this.selectedTemplateType === TemplateTypeId.INSPECTION) {
			groups.push(...Object.values(this.treeNodeMap.map))
		}
        
        for (const group of groups) {
            const lStatus = group.data.color ?? SectionValidationStatus.OK;
            
            if (lStatus === SectionValidationStatus.OK) {
                continue;
            }
            issueNodes[lStatus === SectionValidationStatus.MISSING_DATA ? 'missingValues' : 'abnormalValues']
                .push(group);
            
            if (statusLevels[lStatus] > statusLevels[status]) {
                status = lStatus;
            }
        }
        const sortBy = Utils.createSortFunction<SegmentTreeNode>(n => this.segmentMap[(n.data.id ?? -1)]?.sortOrder ?? 0)
        issueNodes.missingValues.sort(sortBy);
        issueNodes.abnormalValues.sort(sortBy);
        
        this.issueNodes = issueNodes;
        this.noteStatus = status;
    }
    
    private async processValueChanges() {
        const changes = this.calculationDiffer?.changes();
        if (!changes) return;
    
        console.debug('Found changes');
    
        const changesToMake: Function[] = [];
        changes.forEachItem(record => {
            const {currentValue, previousValue, key} = record;
            const input = this.inputs[key as any];
        
            if (!input.templateSegment.calculate)
                return;
        
            changesToMake.push(async () => {
                let value = currentValue;
                switch (input.templateSegmentType) {
                    case TemplateSegmentTypeId.TAG_CHOICE:
                        value = (Array.isArray(currentValue) ? currentValue : (currentValue?.split(',') ?? []))
                            .filter((value: unknown) => typeof value === 'string' && value.trim().length > 0);
                    
                        break;
                
                    case TemplateSegmentTypeId.CHECKBOX:
                        value = typeof currentValue === 'string' ? currentValue.toLowerCase() === 'checked' : null;
                        break;
                
                    case TemplateSegmentTypeId.DATE_TIME:
						value = this.convertValueToDate(currentValue, input.templateSegment.includeDate, input.templateSegment.includeTime);
                        break;
                
                    case TemplateSegmentTypeId.TEXT_CHOICE:
                        if (typeof value !== 'string') return;
                    
                        value = await this.getChoiceListItemOrLike(input.templateSegment.choiceList!.id, value);
                }
                input.setValue(value, { emit: false, validationOptions: { updateErrorMessage: 'alreadySet' } });
            })
        });
        
        if (changesToMake.length > 0)
            changesToMake.push(() => {
                Object.values(this.inputs).forEach(i => this.calculationTooltips[i.templateSegmentId] = this.getCalculatorTooltip(i));
            })
		else {
			console.debug('No changes to perform.');
			return;
		}
    
        console.debug(`Performing ${changesToMake.length - 1} changes.`);
        setTimeout(() => changesToMake.forEach(async fn => await fn?.()));
    }

    private convertValueToDate(input: string, includeDate?: boolean, includeTime?: boolean) {
		if( moment.isMoment(input) )
			return input.toDate();
		if( !input || typeof(input) !== 'string' )
		{
			console.log("Unexpected non-string/moment value in convertValueToDate:");
			console.log(input);
			return input;
		}
		if( includeDate && includeTime )
			return new Date(input);
		if( includeDate )
		{
			const parts = input.split('-');
			if( parts.length != 3 )
				return null;
			return new Date(Number(parts[0]), Number(parts[1])-1, Number(parts[2]));
		}
		if( includeTime )
		{
			const parts = input.split(":");
			if( parts.length == 2 )
				parts.push("0");
			if( parts.length != 3 )
				return null;
			return new Date(1970, 0, 1, Number(parts[0]), Number(parts[1]), Number(parts[2]));
		}
		return null;
	}
    
    private async calculateValues() {
        const valueMap: TemplateValueCalculatorValueMap = {};
        const tagHrefMap: SelectedTagMap = {};
	    const selectedTags: {[tagId: number]: TagValue} = {};
        this.calculationErrors = {};
        
        for (const input of Object.values(this.inputs)) {
            let value = this.calculationValue(input);
            
            if (input.templateSegmentType === TemplateSegmentTypeId.TAG_CHOICE) {
                const tags = Array.isArray(input.value) ? input.value : [];
                tagHrefMap[input.templateSegmentId] = tags.filter(tv => tv?._links)
                    .map((tv: TagValue) => {
						selectedTags[tv.id] = tv;
	                    return tv?._links.self.href;
                    });
            }
        
            valueMap[input.templateSegmentId] = value;
        }
		
		const valueMapSrcBuilder: (() => Record<string | number, any>) = ((orgMap: TemplateValueCalculatorValueMap) => {
			const _orgMap = {...orgMap};
			return () => {
				const newMap = {...valueMap};
				Object.keys(_orgMap).forEach((key) => {
					// Add removed calculated fields back to the map to be evaluated by the value differ
					if (key in newMap) return;
					newMap[key as any] = null;
				});
				return newMap;
			}
		})(valueMap);
        
        this.calculationDiffer = ValueDiffer.fromKeyValueDiffers<string, any>(this.differs, valueMapSrcBuilder);
        
		if( this.segmentDependencyGraph ) // undefined for basic note
		{
	        const promise = this.templateValueCalculator.calculateValues(
	            this.segmentDependencyGraph!,
	            this.segmentMap,
	            valueMap,
		        this.calculationErrors,
	            tagHrefMap,
	            Object.values(selectedTags),
	            this.selectedLog!.id,
	            this.selectedLog!.timeZone,
	            this.getValueCalculatorExternalFields());
	        
	        await promise;
	        await this.checkForValueChanges();
		}
    }

	getValueCalculatorExternalFields() {
		let note = this.getEditorNote();
		const activeUser = LogService.selectedLog?.activeLogUser?.user;
		
		//Properties of note from hard coded values through template editor.
		//See template-formula-editor.js
		return {
			'noteId': note.id,
			'noteTime': note.noteTime !== 'now' ? note.noteTime : undefined,
			'carryForward': note.carryForward,
			'carryForwardEndTime': note.carryForwardEndTime,
			'created': this.editingNote?.created,
			'turnover': this.noteProperties?.turnover,
			'draft': this.editingNote?.draft,
			'currentUserId': activeUser?.id
		} as {[key: string]: any};
	}
    
    getCalculatorTooltip(input: NoteEntryInput): string
    {
        const map = this.segmentMap;
        const segment = map[input.templateSegmentId];
        const node = Utils.arrayToMap((this.segmentDependencyGraph ?? []), i => i.id)?.[input.templateSegmentId];
        
        if( !segment || !segment.calculate || !node )
            return '';
        
        const tsDepends = node.dependsOn
            .filter((depId) => {
                return !node.optionalVars[depId] && isEmpty.call(this, this.inputs[depId]);
            })
            .map((depId) => map[depId].longName);

        const valueCalculatorNoteFields = this.getValueCalculatorExternalFields();
        const extDepends = node.dependsOnExtValues
            .filter(f =>  valueCalculatorNoteFields[f] === undefined || valueCalculatorNoteFields[f] === null);

        const depends = tsDepends.concat(extDepends).join(", ");

        return depends.length ? `${input.templateSegment.name} depends on the following missing fields: ${depends}` : '';

		function isEmpty(this: NoteEntryService, input: NoteEntryInput) {
			if (!input)
				return true;
			const val = this.calculationValue(input);
			return val == null || (Array.isArray(val) && val.length === 0) || (typeof val === 'string' && val?.trim()?.length === 0);
		}
    };
    
    private getDefaultControlValue(segment: TemplateSegment, useDefault = true) {
        switch (segment.type.id) {
            case TemplateSegmentTypeId.TEXT_CHOICE:
				return (useDefault) ? (segment.defaultChoice ?? null) : null;
                
            case TemplateSegmentTypeId.TAG:
                return (useDefault) ? (segment.tagValue?.viewString ?? null) : null;
                
            case TemplateSegmentTypeId.TEXT_INPUT:
                return '';
                
            case TemplateSegmentTypeId.TAG_CHOICE:
            case TemplateSegmentTypeId.FILE_ATTACHMENT:
                return [];
                
            default:
                return null;
        }
    }
    
    getNextSection(direction: 'NEXT' | 'PREVIOUS', requireSections: boolean = true): NoteEntryGroup | null {
        if (!this.selectedGroup)
            return null;
        
        let cIdx = this.groups.indexOf(this.selectedGroup);
        if (cIdx === -1)
            return null;
        
        if (direction === 'NEXT')
        {
            while ((cIdx += 1) < this.groups.length) {
                const newGroup = this.groups[cIdx];
                if (newGroup.controls.length || !requireSections)
                    return newGroup;
            }
        }
        else if (direction === 'PREVIOUS') {
            while ((cIdx -= 1) >= 0) {
                const newGroup = this.groups[cIdx];
                if (newGroup.controls.length || !requireSections)
                    return newGroup;
            }
        }
        return null;
    }

	findNextBestNode(node: SegmentTreeNode): SegmentTreeNode | undefined {
		if (!node) return;
        const group = this.groupsMap[node.data!.id!];

        if (group && group.controls.length) {
            return node;
        } else if (node.children.length) {
            for (const n of node.children) {
                const res = this.findNextBestNode(n);
                if (res)
                    return res;
            }
        }
        return undefined;
    }
	
	processAllValidators(options?: ValidationOptions) {
		Object.values(this.inputs).forEach(input => input.processValueValidators(options));
	}

    private createNodesFromSegments(templateName: string) {
        const map: SegmentTreeNodeMap = { nodes: [], map: {} };

        // create fake section to contain first segments of this template, if this template doesn't
        // already begin with a section element
        if (this.selectedSegments[0]?.type.id !== TemplateSegmentTypeId.SUB_TEMPLATE) {
            const node = new SegmentTreeNode({ id: -1 , level: 1, name: templateName}, this.groupsMap[-1], this.destroy$);
            map.map[-1] = node;
            map.nodes.push(node);
        }

        map.nodes = map.nodes.concat(
            this.selectedSegments
                .filter(s => s.type.id === TemplateSegmentTypeId.SUB_TEMPLATE)
                .sort(Utils.createSortFunction(s => s.sortOrder))
                .map(s => {
                    const n = new SegmentTreeNode(s, this.groupsMap[s.id], this.destroy$);
                    map.map[s.id] = n;
                    return n;
                })
        );

        const findParentIdx = (start: number, level: number, levels: number[]) => {
            for (let i = start; i >= 0; i--) {
                if (levels[i] < level) return i;
            }
            return -1;
        };

        const nodes = map.nodes;
        const levels = nodes.map(n => n.data!.level!);
        for (let i = nodes.length - 1; i >= 0; i--) {
            const node = nodes[i];
            const parentIdx = findParentIdx(i, node.data!.level!, levels);

            if (parentIdx > -1) { // if parent index was found
                const parent = nodes[parentIdx];
                parent.children.splice(0, 0, node);
                node.parent = parent;
            }
        }
		
		const removeEmptyNodes = (nodes: SegmentTreeNode[]) => {
			for (let idx = nodes.length - 1; idx >= 0; idx--) {
				const node = nodes[idx];
				const segments = this.groupsMap[node.data.id]?.controls ?? [];
				
				if (node.children.length) {
					removeEmptyNodes(node.children);
				}
				
				if (!segments.length && !node.children.length) {
					nodes.splice(idx, 1);
					delete map.map[node.data.id];
				}
			}
		}
		
		removeEmptyNodes(map.nodes);
        map.nodes = map.nodes.filter(n => !n.parent);
        this.treeNodeMap = map;
    }

    private getWritableLogs() {
        return combineLatest([
			this.userService.fetchLogUsers({ user: AppStateService.currentUser?.id?.toString() ?? '0'}, LogUserProjections.WITH_LOG),
            this.shiftService.fetchActiveUserShifts()
        ]).pipe(
            map(([logUsers, shifts]) => {
                return logUsers.filter(lu =>
                    !(lu.role.id === LogRoleId.READ ||
                        (lu.role.id === LogRoleId.WRITE_ONSHIFT && !shifts.find(s => s.log.id === lu.log.id)))
                );
            })
        );
    }
    
    private createCommentControls(template: Template) {
        Object.values(this.treeNodeMap.map).forEach(node => {
            const requiredComment = requiresComment(node);
            const group = this.groupsMap[node.data.id];
            group?.controls.forEach(control => {
                if (requiredComment)
                    control.addCommentValidators(TemplateValueValidators.getCommentValidator(control));
                    control.processCommentValidators();
                }
            );
        })
        
        function requiresComment(node: SegmentTreeNode): boolean {
            const nodeValue = (node.data as TemplateSegment)?.requireCommentOnAbnormal ?? null;
            
            if (typeof nodeValue === 'boolean') {
                return nodeValue;
            }
            else if (node.parent) {
                return requiresComment(node.parent);
            }
            return !!template.requireCommentOnAbnormal;
        }
    }
    
    addValueToChoiceListValueMap(listId: number, item: ChoiceListItemLike) {
        if (!item || (item.value?.trim()?.length ?? 0) <= 0)
            return;
        
        this.getChoiceListValueMap(listId)[item.value] = item;
    }
    
    private getChoiceListValueMap(listId: number) {
        return this.choiceListValues[listId] = this.choiceListValues[listId] ?? {};
    }
    
    private async getChoiceListItemOrLike(listId: number, value: string): Promise<ChoiceListItemLike> {
        const valueMap = this.getChoiceListValueMap(listId);
        let item = valueMap[value];
        if (item)
            return item;
        
        item = (await this.choiceListService.fetchItems({
            listId: listId,
            value: value,
            size: 1
        }).toPromise())[0];
        
        return valueMap[value] = (item ?? { id: -1, value: value, abnormal: false });
    }
    
    syncNote(syncType: SyncType): Observable<Note> {
        const editorNote = this.getEditorNote();
        
        if (syncType === SyncType.PUBLISH)
            editorNote.draft = false;
		else if (this.editingPublishedNote) // Syncing disabled on note when the note is not a draft
			return this.editingNote ? of(this.editingNote) : throwError('No note available for sync');
		else if (syncType === SyncType.UPDATE_ONLY && !this.editingNote) 
			return throwError(new Error('Note not set to update'))
	    
	    const startedAsDraft = editorNote.draft;
	
	    /**
	     * Removes the draft property so that if the note has become published during the time of edit the server does not
	     * give an error about co-creators not being assigned to the note.
	     */
	    if (typeof editorNote.id === 'number' && syncType !== SyncType.PUBLISH)
		    delete (editorNote as any).draft;

		const completeSync = (error: NoteSyncErrorFeedback | null = null) => {
			this.syncDate = new Date();
			this.syncingNoteSubject.next(false);
			this.syncErrorFeedbackSubject.next(error);
		};
        
		const updateNote = (note: Note | NoteEntity) => {
			return this.noteService.getLogNote(note.id, NoteProjections.templateValues)
				.pipe(
					switchMap(note => from(this.updateEditorFromNote(note, syncType))),
					switchMap(() => of(note))
				);
		}

		const noteOb$: Observable<Note | NoteEntity> = (syncType === SyncType.UPDATE_ONLY)
			? of(this.editingNote!)
			: ((typeof editorNote.id === 'number')
				? this.http.patch<NoteEntity>(`/api/notes/${editorNote.id}`, editorNote)
				: this.http.post<NoteEntity>('/api/notes', editorNote))
	
	    
	    this.syncingNoteSubject.next(true);
	    
		return noteOb$.pipe(
			tap((note) => {
				if (!note.draft && startedAsDraft) {
					this.noteStatusChangeSubject.next(NoteStatusUpdate.PUBLISHED);
				}
			}),
            switchMap((note) => {
                if (this.selectedTemplateType === TemplateTypeId.OPERATIONAL) {
                    const selectedSegment = this.selectedSegments.find(segment => segment.id === this.selectedGroup?.segmentId);
					
					const currentTime = this.noteProperties?.noteTime ?? null;
					this.clearValues();
					this.setupNotePropertiesControls(this.selectedTemplate);
                    if (!selectedSegment?.clearTime && this.noteProperties) {
						this.noteProperties.noteTime = currentTime;
						
	                    // Emits the updated properties to correctly update UI on note-entry-controls
						this.notePropertiesSubject.next(this.noteProperties);
                    }
    
                    return this.noteService.getLogNote(note.id, NoteProjections.BASE);
                }
				
				if (syncType === SyncType.PUBLISH)
					return this.noteService.getLogNote(note.id, NoteProjections.BASE);
				
				return updateNote(note);
            }),
			tap(() => completeSync(null)),
			catchError((e: Error | HttpErrorResponse) => {
				let errorFeedback = 'An error occurred while saving the note';
				if (e instanceof HttpErrorResponse) {
					if (e.status === 403) {
						this.setSelectedTemplate(undefined);
						this.noteStatusChangeSubject.next(NoteStatusUpdate.ACCESS_REVOKED);
					}
					if (typeof e.error === 'string') {
						errorFeedback = e.error;
					}
					else if (Array.isArray(e.error?.errors)) {
						errorFeedback = e.error.errors.map((e: any) => e?.message ?? '').join(', ')
					}
					console.error(e.error);
				}
				completeSync({message: errorFeedback, object: e});

				// Suppress the error toast as throwing the error here will catch at the error service
				// and the user will already be presented with the error
				(e as CustomErrorProperties).suppressErrorToast = true;
				return throwError(e);
			}) as any,
            takeUntil(
				this.isSyncingNote$.pipe(
					skip(1),
					filter(isSyncing => isSyncing) // Only take when the status is updated to true again so that setting to false on complete doesn't kill subscribers
				))
            )
    }
    
    getEditorNote() {
        const note = { ...this.entryNote };
        note.id = this.editingNote?.id;
        
        const noteTime = this.noteProperties?.noteTime;
        if (noteTime instanceof Date)
            noteTime.setSeconds(0, 0);
        
        note.noteTime = (noteTime instanceof Date) ? noteTime.toISOString() : 'now';
        note.carryForward = !!this.noteProperties?.carryforward.enabled;
        note.carryForwardEndTime = (this.noteProperties?.carryforward.endDate instanceof Date)
	        ? this.noteProperties.carryforward.endDate.toISOString()
	        : null;
		
        note.turnover = !!this.noteProperties?.turnover;
    
        if (!note.carryForward)
            note.carryForwardEndTime = null;
        
        // Basic Note
        if (!this.entryNote.template) {
            const useRichText = this.noteProperties?.note?.useRichText ?? false;
            const value = this.noteProperties?.note?.model ?? '';
            note.richText = useRichText ? value : null;
            note.note = !useRichText ? value : null;
        }
        // Templated Note
        else {
            note.note = 'NA'

			note.noteTemplateValues = Object.values(this.inputs).reduce((arr, input) => {
				const oldNtv = this.ntvMap[input.templateSegmentId];
				const newNtv = input.getNoteEditorEntryValue(oldNtv);
				const segment = this.segmentMap[newNtv.templateSegmentId];

				if (!segment.calculate && NoteEntryTemplateValue.hasValue(newNtv))
					arr.push(newNtv);
				
				return arr;
			}, [] as NoteEntryTemplateValue[])
            
			if (!this.editingPublishedNote)
				NoteEntryNote.updateCollection(note, 'coCreators',
					getArray(this.editingNote?.coCreators), this.coCreators.filter(l => l.selected));
        }
    
        NoteEntryNote.updateCollection(note, 'tagValues',
            getArray(this.editingNote?.tagValues), getArray(this.noteProperties?.tagValues));
        
        
        NoteEntryNote.updateCollection(note, 'attachments',
            getArray(this.editingNote?.attachments), getArray(this.noteProperties?.attachments));
        
        return note;
    
        function getArray(val: any) {
            return Array.isArray(val) ? val : [];
        }
    }
    
    async updateEditorFromNote(note: Note.Projections.TemplateValues, type: SyncType) {
		this.editingNote = note;
        
		console.debug(this.editingNote);

        this.ntvMap = Utils.arrayToMap(this.editingValues, ntv => ntv.templateSegmentId);
        this.entryNote = NoteEntryNote.fromNote(note);
        
        const coCreatorMap = Utils.arrayToMap(note.coCreators, lgUsr => lgUsr.id);
        this.coCreators.forEach(lgUsr => lgUsr.selected = lgUsr.id in coCreatorMap);
        
        if (this.noteProperties) {
            this.noteProperties.attachments = Array.from(note.attachments);
            this.noteProperties.tagValues = Array.from(note.tagValues);
    
            const noteTime = new Date(note.noteTime);
            const created = new Date(note.created);
            const secondsDiff = Math.abs((noteTime.getTime() - created.getTime()) / 1000);
            
            // If the difference in noteTime and creation time is greater than 59 seconds and the note time ends in 00, consider it to be user input for editing draft.
            if ((secondsDiff > 59 && noteTime.getSeconds() === 0) || !note.draft) {
                this.noteProperties.noteTime = noteTime;
            }
            else {
                this.noteProperties.noteTime = null;
            }
    
            this.noteProperties.turnover = this.editingNote.turnover;
            this.noteProperties.carryforward.enabled = this.editingNote.carryForward;
            this.noteProperties.carryforward.endDate = note.carryForwardEndTime == null ? null : new Date(note.carryForwardEndTime);
    
            const useRichText = !!note.richText;
            this.noteProperties.note.useRichText = useRichText;
            this.noteProperties.note.model = ((useRichText ? note.richText : note.note) ?? '');
        }

		const editingValuesMap = Utils.arrayToMap(this.editingValues, ntv => ntv.templateSegmentId);
		const listFetch = new ChoiceListItemFetch();

		for (const control of Object.values(this.inputs)) {
			const ntv: NoteTemplateValue = editingValuesMap[control.templateSegmentId];
			let valueUpdate: any;
			let commentUpdate: string | null = null;

			if (ntv) {
				if (!control.entered || new Date(ntv.updated) > control.entered) {
					valueUpdate = NoteEntryTemplateValue.getEditorValueFromValue(ntv, control.templateSegment);
                switch (control.templateSegmentType) {
                    case TemplateSegmentTypeId.TEXT_INPUT:
                        control.extras['useRichText'] = !!ntv.richTextValue;
                        break;
                        
                    case TemplateSegmentTypeId.TEXT_CHOICE:
						if (valueUpdate != null && typeof valueUpdate === 'string' && valueUpdate.trim().length > 0) {
							const valueMap = this.getChoiceListValueMap(control.templateSegment.choiceList!.id);
							if (valueMap[valueUpdate]) {
								valueUpdate = [valueMap[valueUpdate]];
							}
							else if (valueUpdate) {
								listFetch.add(control.templateSegment.choiceList!.id, valueUpdate).then((item) => {
									valueMap[item.value] = item;
									control.setValue([item], { date: null, emit: false, validationOptions: { updateErrorMessage: 'alreadySet'} });
								});
								valueUpdate = undefined;
							}
						}
					}
				}
				if (!control.commentEntered || !!(ntv.commentEntered && new Date(ntv.commentEntered) > control.commentEntered)) {
					commentUpdate = ntv.comment ?? '';
                }
			}
			else if (!control.entered) {
				valueUpdate = this.getDefaultControlValue(control.templateSegment, false)
			}

			if (valueUpdate !== undefined) {
				control.setValue(valueUpdate, { date: null, emit: false, validationOptions: { updateErrorMessage: 'alreadySet'} });
			}
			if (commentUpdate != null) {
				control.setComment(commentUpdate, { date: null, emit: false });
			}
			control.setCommentAttachments(ntv?.commentAttachments ?? [], { emit: false })
		}
		await listFetch.execute(this.choiceListService);
		this.updateLogUsersCoCreatorSelection(this.logUsers);
		this.noteProperties = this.noteProperties; // Sets note properties to itself to trigger subscription updates for property changes that do not get detected
		this.calculateValues();
    }
}